/**
 * Reading a whole collection when the data plane will only hand over 1000 rows
 * at a time.
 *
 * Lifted out of `TalentsQueue` so it can be tested. The queue is the one view
 * whose order is derived over the entire set — a page of 1000 sorted by
 * `strength_score` is not the first 1000 of "whoever has been waiting longest"
 * — and a read above the cap is *refused* rather than clamped, which is how the
 * Candidatos list came to render empty against a database holding every
 * candidate.
 */

/** The data plane's hard ceiling. A read asking for more is refused outright. */
export const PAGE_SIZE = 1000;

export interface TalentQuery {
    /** The toolbar's filters, in `find`'s own `where` shape. */
    filterValues?: Record<string, unknown>;
    /** The toolbar's sort keys, most significant first. */
    sortBy?: unknown[];
    /** The toolbar's search box. */
    search?: string;
}

interface PageResult {
    data?: unknown[];
    meta?: { total?: unknown };
}

interface Collection {
    find(query: Record<string, unknown>): Promise<PageResult | undefined>;
}

interface DataAccessor {
    collection(slug: string): Collection;
}

/**
 * Every row matching the toolbar's query, walked in pages.
 *
 * The filters, sort and search are passed through untouched — deliberately the
 * live objects rather than a JSON round-trip of them. That round-trip is a bug
 * this function exists to have fixed: `JSON.parse(JSON.stringify(filters))`
 * turns a `Date` into a string and drops `undefined` entirely, so filtering the
 * queue by any date property would have sent the driver something it never
 * agreed to accept. Serialising is fine for deciding *when* to re-run; it is
 * not fine for deciding *what to ask*.
 *
 * Two independent stop conditions, because either alone is wrong:
 *
 *   • **A short page** is the real end of the data, and the primary guard.
 *   • **The total, when the server reports one**, catches a server that ignores
 *     `offset` and would otherwise return a full page forever.
 *
 * The total is only consulted when it is actually a number. Defaulting a
 * missing total to the rows-so-far — which is what the older `fetchAllRows`
 * does — makes the comparison trivially true and stops after the first full
 * page, truncating silently at exactly the boundary this function exists to
 * cross.
 */
export async function fetchAllTalents(
    data: DataAccessor,
    query: TalentQuery = {}
): Promise<Record<string, unknown>[]> {
    const all: Record<string, unknown>[] = [];

    for (let offset = 0; ; offset += PAGE_SIZE) {
        const res = await data.collection("talents").find({
            ...(query.filterValues && Object.keys(query.filterValues).length
                ? { where: query.filterValues }
                : {}),
            ...(query.sortBy?.length ? { orderBy: query.sortBy } : {}),
            ...(query.search ? { searchString: query.search } : {}),
            limit: PAGE_SIZE,
            offset
        });

        const page = (res?.data ?? []) as Record<string, unknown>[];
        all.push(...page);

        if (page.length < PAGE_SIZE) break;

        const total = typeof res?.meta?.total === "number" ? res.meta.total : null;
        if (total !== null && all.length >= total) break;
    }

    // `id` is read as a string everywhere downstream — the status overlay keys
    // on it and the entity lookup compares against it — so it is normalised
    // once here rather than at each comparison.
    return all.map(row => ({ ...row, id: String(row.id) }));
}
